import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, model } from '@angular/core';

export interface SegmentedOption {
  value: string;
  label: string;
}

/**
 * `<nb-segmented>` — a single-select segmented control (Mockup `.seg`).
 *
 * An accessible `radiogroup`: arrow keys move and select with roving tabindex,
 * Home/End jump to ends, the selected segment is the only tab stop. Two-way
 * bind `value`.
 *
 * @example
 * <nb-segmented [options]="[{value:'list',label:'Liste'},{value:'kart',label:'Kart'}]"
 *               [(value)]="visning" ariaLabel="Visning" />
 */
@Component({
  selector: 'nb-segmented',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'radiogroup',
    '[attr.aria-label]': 'ariaLabel() || null',
    '(keydown)': 'onKeydown($event)',
  },
  template: `@for (opt of options(); track opt.value; let i = $index) {
    <button
      type="button"
      role="radio"
      [class.on]="opt.value === value()"
      [attr.aria-checked]="opt.value === value()"
      [attr.tabindex]="tabindexFor(opt.value, i)"
      (click)="select(opt.value)"
    >
      {{ opt.label }}
    </button>
  }`,
  styleUrl: './segmented.css',
})
export class NbSegmented {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly options = input.required<SegmentedOption[]>();
  readonly value = model<string>();
  readonly ariaLabel = input<string>();

  private readonly selectedIndex = computed(() => {
    const i = this.options().findIndex((o) => o.value === this.value());
    return i < 0 ? 0 : i;
  });

  protected tabindexFor(value: string, index: number): number {
    const sel = this.value();
    // Roving tabindex: the selected segment is the tab stop, else the first.
    if (sel === undefined) return index === 0 ? 0 : -1;
    return value === sel ? 0 : -1;
  }

  protected select(value: string): void {
    this.value.set(value);
  }

  protected onKeydown(e: KeyboardEvent): void {
    const opts = this.options();
    if (!opts.length) return;
    let next = this.selectedIndex();
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (next + 1) % opts.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (next - 1 + opts.length) % opts.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = opts.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    this.value.set(opts[next].value);
    const buttons = this.host.nativeElement.querySelectorAll<HTMLButtonElement>('button');
    buttons[next]?.focus();
  }
}
